import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, NgZone, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IonItem, IonInput, IonSpinner, IonIcon, IonToggle } from '@ionic/angular/standalone';
import { SexyModule } from '@qid/sexy';
import { SuperButtonComponent } from '@qid/super';
import { Address } from '../../../../interface/address.inteface';
import { Plan, PlanPricingModel } from '../../../plan';
import { TimePeriod } from '../../../../enum/time-period.enum';
import { OrderApi } from '../../services/order.api';
import { CreateOrderDto } from '../../dto/create-order.dto';
import { OrderQuote } from '../../dto/create-order-quote.dto';
import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AddressFillService } from '../../../../services/address-fill.service';
import { AuthSelectors } from '../../../authentication';
import { Store } from '@ngrx/store';
import { User, UsersSelectors } from '../../../user';
import { OrderStatus } from '../../enum/order-status.enum';
import { Order } from '../../interface/order.interface';
import { OrderPriceBreakup } from '../../interface/order-price.breakup.interface';
import { RazorpayResponse, RazorpayPaymentFailedResponse } from '../../../../interface/razorpay-response.interface';
import { ENVIRONMENT, Environment } from '@qid/env';

declare global {
  interface Window {
    Razorpay: any;
  }
}

class CheckoutError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'CheckoutError';
  }
}

type PaymentPhase = 'checkout' | 'processing' | 'success' | 'failed';

export interface CheckoutLineItem {
  organisation: string;
  organisationName?: string;
  plan: Plan;
}

@Component({
  selector: 'checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SexyModule,
    SuperButtonComponent,
    IonItem,
    IonInput,
    IonSpinner,
    IonIcon,
    IonToggle,
  ]
})
export class CheckoutComponent implements OnChanges, OnDestroy {

  @Input() plan: Plan;
  @Input() paymentTerms: TimePeriod;
  @Input() items: CheckoutLineItem[] | null = null;
  /** true → razorpay_subscription (mandate); false → one-time razorpay. */
  @Input() isAutoRenew = true;

  @Output() onSuccess: EventEmitter<void> = new EventEmitter<void>();
  // Emitted once payment starts processing so the host can hide its back button (the flow can't be cancelled from here).
  @Output() processingStateChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  planPricingModel: PlanPricingModel;
  billingForm: FormGroup;
  public user$: Observable<User> = this.store.select(UsersSelectors.selectAuthenticatedUser);
  public order: Order;
  public quote: OrderQuote | null = null;
  // Locally computed for the order summary so we don't pre-create an order just to display it.
  public priceBreakup: OrderPriceBreakup;
  private static readonly GST_RATE = 0.18;
  public paymentPhase: PaymentPhase = 'checkout';
  public paymentErrorMessage: string | null = null;
  public isFetchingAddress = false;
  public manualAddressEntry = false;
  /** True once we've waited long enough without confirmation — show Recheck status. */
  public showRecheckStatus = false;

  private destroy$ = new Subject<void>();
  private paymentStatusTimeout?: number;
  private successRedirectTimeout?: number;
  private paymentWaitTimeout?: number;
  // Poll fast initially, then back off — but keep polling until the payment is confirmed or fails.
  private readonly FAST_POLL_ATTEMPTS = 30;
  private readonly PAYMENT_CHECK_INTERVAL = 3000;
  private readonly SLOW_PAYMENT_CHECK_INTERVAL = 10000;
  private readonly PAYMENT_WAIT_TIMEOUT_MS = 30_000;
  protected readonly TimePeriod = TimePeriod;

  constructor(
    @Inject(ENVIRONMENT) private environment: Environment,
    private orderApi: OrderApi,
    private store: Store,
    private zone: NgZone,
    private fb: FormBuilder,
    private addressFillService: AddressFillService
  ) {
    this.billingForm = this.fb.group({
      billingName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      addressLine: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(200)]],
      city: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      state: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      country: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      zipCode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      gstin: ['', [Validators.pattern(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/)]]
    });

    this.billingForm.get('zipCode').valueChanges
      .pipe(debounceTime(800), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((pincode: string) => this.fillAddressFromPincode(pincode));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['plan']?.currentValue || changes['items']?.currentValue || changes['paymentTerms']) {
      if (this.items?.length || this.plan) {
        void this.setup();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.paymentStatusTimeout) {
      clearTimeout(this.paymentStatusTimeout);
    }
    if (this.successRedirectTimeout) {
      clearTimeout(this.successRedirectTimeout);
    }
    this.clearPaymentWaitTimeout();
  }

  private async setup(): Promise<void> {
    this.clearPaymentWaitTimeout();
    this.paymentPhase = 'checkout';
    this.showRecheckStatus = false;
    this.order = undefined;
    this.quote = null;

    if (this.items?.length) {
      const response = await this.orderApi.quote({
        period: this.paymentTerms,
        items: this.items.map((item) => ({
          organisation: item.organisation,
          plan_id: item.plan._id,
        })),
      });
      this.quote = response.data || null;
      this.priceBreakup = this.quote?.priceBreakup;
      if (!this.quote?.priceBreakup) {
        throw new CheckoutError('Failed to quote order', 'QUOTE_FAILED');
      }
      await this.fetchOrgDetails();
      return;
    }

    this.planPricingModel = this.getPricing(this.plan);

    if (!this.planPricingModel) {
      throw new CheckoutError('Plan pricing model not found for the selected payment terms', 'PLAN_PRICING_NOT_FOUND');
    }

    // Compute the summary breakup locally. The order itself is created only once,
    // at payment time, so we never leave an orphaned pre-created order behind.
    this.priceBreakup = this.computePriceBreakup(this.planPricingModel);

    await this.fetchOrgDetails();
  }

  /** Mirrors the backend price calculation (18% GST) for display in the order summary. */
  private computePriceBreakup(pricingModel: PlanPricingModel): OrderPriceBreakup {
    const round = (amount: number) => Math.round(amount * 100) / 100;
    const amountBeforeTax = (pricingModel.amount || 0) - (pricingModel.discountAmount || 0);
    return {
      subtotal: round(pricingModel.amount || 0),
      discountAmount: round(pricingModel.discountAmount || 0),
      amountBeforeTax: round(amountBeforeTax),
      taxAmount: round(amountBeforeTax * CheckoutComponent.GST_RATE),
      payableAmount: round(amountBeforeTax * (1 + CheckoutComponent.GST_RATE)),
    };
  }

  getPricing(plan: Plan): PlanPricingModel | undefined {
    const period = this.paymentTerms?.toLowerCase();
    return plan?.planPricingModels?.find((pricing) =>
      pricing.timePeriod?.toLowerCase() === period ||
      pricing.name?.toLowerCase() === period
    );
  }

  getItemAmount(item: CheckoutLineItem): number {
    const line = this.quote?.items?.find((quoted) => quoted.organisation === item.organisation);
    return line?.priceBreakup?.subtotal ?? this.getPricing(item.plan)?.amount ?? 0;
  }

  private async fillAddressFromPincode(pincode: string): Promise<void> {
    if (!pincode || !/^\d{6}$/.test(pincode)) {
      return;
    }

    this.isFetchingAddress = true;
    try {
      const country = this.billingForm.get('country').value || 'India';
      const result = await this.addressFillService.validatePostalCode(pincode, country);

      this.zone.run(() => {
        if (result.isValid && (result.city || result.state)) {
          this.billingForm.patchValue({
            city: result.city || '',
            state: result.state || '',
            country
          });
          this.billingForm.get('city').markAsDirty();
          this.billingForm.get('state').markAsDirty();
          this.billingForm.get('country').markAsDirty();
          this.manualAddressEntry = false;
        } else {
          this.manualAddressEntry = true;
        }
      });
    } catch (error) {
      console.error('Error fetching address from pincode:', error);
      this.zone.run(() => {
        this.manualAddressEntry = true;
      });
    } finally {
      this.zone.run(() => {
        this.isFetchingAddress = false;
      });
    }
  }

  shouldShowError(fieldName: string): boolean {
    const control = this.billingForm.get(fieldName);
    if (!control) {
      return false;
    }
    return control.invalid && (control.touched || control.dirty || control.value);
  }

  private async fetchOrgDetails(): Promise<void> {
    try {
      const activeOrg = await firstValueFrom(
        this.store.select(AuthSelectors.selectActiveAccount).pipe(takeUntil(this.destroy$))
      );

      if (activeOrg?.organisationDetails) {
        const orgDetails = activeOrg.organisationDetails;

        this.billingForm.patchValue({
          billingName: orgDetails.name || activeOrg.name || '',
          addressLine: orgDetails.addressLine || '',
          city: orgDetails.city || '',
          state: orgDetails.state || '',
          country: orgDetails.country || '',
          zipCode: orgDetails.pinCode || '',
          gstin: orgDetails.gst || ''
        });
      }
    } catch (error) {
      console.error('Error fetching organization details:', error);
    }
  }

  private getFormErrors(): string[] {
    const errors: string[] = [];

    Object.keys(this.billingForm.controls).forEach(key => {
      const control = this.billingForm.controls[key];
      if (control.errors && (control.touched || control.dirty)) {
        if (control.errors['required']) {
          errors.push(`${this.getFieldDisplayName(key)} is required`);
        } else if (control.errors['minlength']) {
          errors.push(`${this.getFieldDisplayName(key)} must be at least ${control.errors['minlength'].requiredLength} characters`);
        } else if (control.errors['maxlength']) {
          errors.push(`${this.getFieldDisplayName(key)} must not exceed ${control.errors['maxlength'].requiredLength} characters`);
        } else if (control.errors['pattern']) {
          if (key === 'zipCode') {
            errors.push('Zip Code must be 6 digits');
          } else if (key === 'gstin') {
            errors.push('GSTIN format is invalid');
          }
        }
      }
    });

    return errors;
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: Record<string, string> = {
      billingName: 'Billing Name',
      addressLine: 'Street Address',
      city: 'City',
      state: 'State',
      country: 'Country',
      zipCode: 'Zip Code',
      gstin: 'GSTIN'
    };
    return displayNames[fieldName] || fieldName;
  }

  private async createOrder(): Promise<void> {
    const formValue = this.billingForm.value;
    const billingAddress: Address = {
      addressLine: formValue.addressLine,
      dist: formValue.city,
      state: formValue.state,
      country: formValue.country,
      zipCode: formValue.zipCode
    };

    const createOrderDto: CreateOrderDto = {
      isAutoRenew: this.isAutoRenew,
      period: this.paymentTerms,
      billingName: formValue.billingName,
      billingAddress,
      gstin: formValue.gstin || '',
      items: this.items?.length
        ? this.items.map((item) => ({
            organisation: item.organisation,
            plan_id: item.plan._id,
          }))
        : [{ plan_id: this.plan._id }],
    };

    const response = await this.orderApi.createSubscriptionOrder(createOrderDto);
    if (!response.data) {
      throw new CheckoutError('Failed to create order', 'ORDER_CREATION_FAILED');
    }

    this.order = response.data;
  }

  private pickOrderField(...keys: string[]): string | undefined {
    const raw = this.order as unknown as Record<string, unknown>;
    for (const key of keys) {
      const value = raw?.[key];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
    return undefined;
  }

  private razorpayOrderId(): string | undefined {
    return this.pickOrderField('razorpayOrderId', 'razorpay_order_id');
  }

  private razorpaySubscriptionId(): string | undefined {
    return this.pickOrderField('razorpaySubscriptionId', 'razorpay_subscription_id');
  }

  private async ensureRazorpayLinked(): Promise<void> {
    if (!this.isAutoRenew) {
      return;
    }

    const needsLink = !this.razorpaySubscriptionId()
      || (!!this.items?.length && !this.order?.razorpayShortUrl);

    if (!needsLink) {
      return;
    }

    const response = await this.orderApi.linkRazorpay(this.order._id);
    if (!response.data) {
      throw new CheckoutError('Failed to link Razorpay subscription', 'RAZORPAY_LINK_FAILED');
    }
    this.order = response.data;
  }

  private async startRazorpayPayment(): Promise<void> {
    if (this.isAutoRenew && this.items?.length) {
      this.redirectToRazorpayShortUrl();
      return;
    }

    await this.openRazorpay();
  }

  private redirectToRazorpayShortUrl(): void {
    if (!this.order?.razorpayShortUrl) {
      throw new CheckoutError('Razorpay payment link is not present', 'RAZORPAY_SHORT_URL_MISSING');
    }

    void this.setPaymentStatusListener();
    const opened = window.open(this.order.razorpayShortUrl, '_blank', 'noopener');
    if (!opened) {
      window.location.assign(this.order.razorpayShortUrl);
    }
  }

  private isPaidStatus(status?: OrderStatus | string): boolean {
    return status === OrderStatus.SUBSCRIPTION_OPEN || status === OrderStatus.PAID;
  }

  private markPaymentSuccess(): void {
    this.clearPaymentWaitTimeout();
    this.showRecheckStatus = false;
    if (this.paymentStatusTimeout) {
      clearTimeout(this.paymentStatusTimeout);
      this.paymentStatusTimeout = undefined;
    }
    this.paymentPhase = 'success';
    this.successRedirectTimeout = window.setTimeout(() => {
      this.onPaymentComplete();
    }, 3000);
  }

  private async openRazorpay(): Promise<void> {
    const subscriptionId = this.isAutoRenew ? this.razorpaySubscriptionId() : undefined;
    const orderId = !this.isAutoRenew ? this.razorpayOrderId() : undefined;

    if (!subscriptionId && !orderId) {
      throw new CheckoutError(
        this.isAutoRenew
          ? 'Razorpay subscription id is not present'
          : 'Razorpay order id is not present',
        this.isAutoRenew ? 'RAZORPAY_SUBSCRIPTION_MISSING' : 'RAZORPAY_ORDER_MISSING',
      );
    }

    if (!window.Razorpay) {
      throw new CheckoutError('Razorpay SDK not loaded', 'RAZORPAY_SDK_MISSING');
    }

    if (!this.environment.razorpayKey) {
      throw new CheckoutError('Razorpay key is not configured in the environment', 'RAZORPAY_KEY_MISSING');
    }

    const user = await firstValueFrom(this.user$.pipe(takeUntil(this.destroy$)));

    const options = {
      key: this.environment.razorpayKey,
      ...(subscriptionId
        ? { subscription_id: subscriptionId }
        : { order_id: orderId }),
      name: 'Oneqid Technologies Private Limited',
      prefill: {
        name: user.name || '',
        email: user.email || '',
        contact: user.phoneNumber || ''
      },
      handler: (response: RazorpayResponse) => {
        this.handleRazorpayResponse(response);
      },
      modal: {
        ondismiss: () => { }
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (response: RazorpayPaymentFailedResponse) => {
      this.zone.run(() => this.handlePaymentFailed(response));
    });
    rzp.open();
  }

  private handleRazorpayResponse(response: RazorpayResponse): void {
    if (response.razorpay_payment_id) {
      void this.setPaymentStatusListener();
    } else {
      console.warn('No payment ID in Razorpay response');
    }
  }

  private handlePaymentFailed(response: RazorpayPaymentFailedResponse): void {
    this.failPayment(response?.error?.description);
  }

  /** Moves the flow to the failed state: stop polling, show the reason, and re-enable navigation. */
  private failPayment(message?: string): void {
    if (this.paymentStatusTimeout) {
      clearTimeout(this.paymentStatusTimeout);
      this.paymentStatusTimeout = undefined;
    }
    this.clearPaymentWaitTimeout();
    this.showRecheckStatus = false;
    this.paymentErrorMessage = message
      || 'Your payment could not be completed. No money has been deducted. Please try again.';
    this.paymentPhase = 'failed';
    this.processingStateChange.emit(false);
  }

  /** Return to the billing form so the user can review details and attempt payment again. */
  retryPayment = (): void => {
    this.paymentErrorMessage = null;
    this.showRecheckStatus = false;
    this.paymentPhase = 'checkout';
  };

  recheckPaymentStatus = async (): Promise<void> => {
    if (!this.order?._id) {
      throw new CheckoutError('Order ID is missing', 'ORDER_ID_MISSING');
    }

    const response = await this.orderApi.recheckPayment(this.order._id);
    const status = response.data?.orderStatus;

    if (this.isPaidStatus(status)) {
      this.markPaymentSuccess();
      return;
    }

    if (status === OrderStatus.FAILED) {
      this.failPayment();
      return;
    }

    // Still pending — keep processing UI and button so the user can try again.
  };

  private clearPaymentWaitTimeout(): void {
    if (this.paymentWaitTimeout) {
      clearTimeout(this.paymentWaitTimeout);
      this.paymentWaitTimeout = undefined;
    }
  }

  private async setPaymentStatusListener(): Promise<void> {
    this.zone.run(() => {
      this.paymentPhase = 'processing';
      this.showRecheckStatus = false;
      this.processingStateChange.emit(true);
    });

    this.clearPaymentWaitTimeout();
    this.paymentWaitTimeout = window.setTimeout(() => {
      this.zone.run(() => {
        if (this.paymentPhase === 'processing') {
          this.showRecheckStatus = true;
        }
      });
    }, this.PAYMENT_WAIT_TIMEOUT_MS);

    let attempts = 0;

    // Keep polling until the order is confirmed (subscription_open or paid). Failures are surfaced
    // separately via Razorpay's `payment.failed` event, which stops this loop. After
    // PAYMENT_WAIT_TIMEOUT_MS we also offer Recheck status, but polling continues so a late webhook
    // still resolves. Polling stops on destroy.
    const scheduleNext = (): void => {
      // If the payment failed in the meantime, stop polling.
      if (this.paymentPhase !== 'processing') {
        return;
      }
      const interval = attempts < this.FAST_POLL_ATTEMPTS
        ? this.PAYMENT_CHECK_INTERVAL
        : this.SLOW_PAYMENT_CHECK_INTERVAL;
      this.paymentStatusTimeout = window.setTimeout(checkPaymentStatus, interval);
    };

    const checkPaymentStatus = async (): Promise<void> => {
      if (this.paymentPhase !== 'processing') {
        return;
      }
      try {
        if (!this.order?._id) {
          throw new CheckoutError('Order ID is missing', 'ORDER_ID_MISSING');
        }

        const response = await this.orderApi.readOne(this.order._id);

        if (this.isPaidStatus(response.data?.orderStatus)) {
          this.zone.run(() => this.markPaymentSuccess());
          return;
        }

        // The backend marks the order FAILED from Razorpay's failure webhooks — resolve the poll here too,
        // in case the client-side `payment.failed` event never fired (e.g. tab regained focus late).
        if (response.data?.orderStatus === OrderStatus.FAILED) {
          this.zone.run(() => this.failPayment());
          return;
        }

        attempts++;
        scheduleNext();
      } catch (error) {
        console.error('Error checking payment status:', error);
        attempts++;
        scheduleNext();
      }
    };

    void checkPaymentStatus();
  }

  onPaymentComplete = (): void => {
    if (this.successRedirectTimeout) {
      clearTimeout(this.successRedirectTimeout);
      this.successRedirectTimeout = undefined;
    }
    this.onSuccess.emit();
  };

  completePaymentClicked = async (): Promise<void> => {
    if (this.billingForm.invalid) {
      this.billingForm.markAllAsTouched();
      const errors = this.getFormErrors();
      const message = errors.length ? errors.join(', ') : 'Please fill all required fields correctly';
      throw new CheckoutError(message, 'FORM_VALIDATION_ERROR');
    }

    try {
      await this.createOrder();
      await this.ensureRazorpayLinked();
      await this.startRazorpayPayment();
    } catch (error) {
      console.error('Error completing payment:', error);
      throw error;
    }
  };
}
